<template>
  <!-- Error Message -->
  <PageError v-if="error" :error="error" :errorCode="errorCode" />

  <div v-else class="p-6 dark:text-white">
    <div v-if="addingDevice"
      class="flex items-center justify-center inset-0 fixed bg-black/50 backdrop-blur-sm z-40"
      @click.self="addingUser = false"
    >
    <div class="p-6 glass-card mb-2 w-[80%] max-w-md min-h-[35%] shadow-xl z-50 flex flex-col justify-between">

      <div>
        <h2 class="text-lg font-semibold mb-1">Add New Device</h2>
        <div class="space-y-4">
          <div>
            <label class="block text-xs uppercase tracking-widest mb-2">Device MAC address (format: XX:XX:XX:XX:XX:XX):</label>
            <input 
              type="text"
              v-model="newDevice.mac_address"
              class="w-full py-2 px-2"
            >
          </div>
        </div>
      </div>

      <div>
        <div class="h-px bg-white/10 mb-5" ></div>
        <div class="flex gap-3 justify-end">
          <button
            @click="addingDevice = false"
            class="px-5 py-2.5 rounded-xl text-sm font-medium transition-all"
          >
            Cancel
          </button>
          <button
            @click="createNewDevice"
            class="btn text-sm font-semibold text-white hover:opacity-90 active:scale-95 transition-all"
          >
            Save Changes
          </button>
        </div>
      </div>

    </div>
  </div>


    <!-- Stats Cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 h-48">
      <div class="glass-card-lite p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-gray-600 dark:text-gray-400 text-sm mb-1">TOTAL DEVICES</p>
            <p class="text-3xl font-bold">{{ devices.length }}</p>
          </div>
          <div class="bg-green-500/20 p-3 rounded-lg">
            <CpuIcon class="text-green-500"/>
          </div>
        </div>
      </div>

      <div class="glass-card-lite p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-gray-600 dark:text-gray-400 text-sm mb-1">ONLINE</p>
            <p class="text-3xl font-bold">{{ onlineCount }}</p>
          </div>
          <div class="bg-blue-500/20 p-3 rounded-lg">
            <Wifi class="text-blue-500"/>
          </div>
        </div>
      </div>

      <div class="glass-card-lite p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-gray-600 dark:text-gray-400 text-sm mb-1">OFFLINE</p>
            <p class="text-3xl font-bold">{{ offlineCount }}</p>
          </div>
          <div class="bg-yellow-500/20 p-3 rounded-lg">
            <WifiOff class="text-yellow-500"/>
          </div>
        </div>
      </div>

      <div class="glass-card-lite p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-gray-600 dark:text-gray-400 text-sm mb-1">ALERTS</p>
            <p class="text-3xl font-bold">{{ alertCount }}</p>
          </div>
          <div class="bg-red-500/20 p-3 rounded-lg">
            <AlertTriangle class="text-red-500"/>
          </div>
        </div>
      </div>
    </div>

    <!-- Header Section -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 glass-card-lite p-6 h-32">
      <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
        Devices
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>

      <div class="flex space-x-3">
        <button
          type="button"
          class="hover:bg-transparent bg-transparent"
          @click="showHowToDevice = true"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="dark:text-gray-50 text-gray-900">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
            <path d="M12 17h.01"/>
          </svg>
        </button>
        <button
          @click="fetchDevices"
          class="btn bg-gray-700 text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Refresh
        </button>
        <button v-if="canAdd || isStaff"

          @click="addingDevice = true"
          class="btn bg-blue-700 text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Add Device
        </button>
      </div>
    </div>

    <!-- Search and Filter Section -->
    <div class="flex gap-4 mb-6">
      <!-- Search Bar -->
      <div class="w-full">
        <SearchBar
          v-model="searchQuery"
          placeholderText="Search devices..."
        />
      </div>

      <!-- Bulk Action Section -->
      <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <GlassSelect
          v-model="bulkAction"
          :options="canDelete || user?.is_staff ? [{ value: 'delete', label: 'Delete selected items' }] : []"
          placeholder="Bulk Actions"
          :valueKey="'value'"
          :labelKey="'label'"
        />
        <button
          @click="applyBulkAction"
          :disabled="!bulkAction || selectedDevices.length === 0"
          :class="[
            'w-full sm:w-auto btn focus:outline-none focus:ring-2 focus:ring-blue-500 text-base',
            bulkAction && selectedDevices.length
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          ]"
        >
          Apply
        </button>
      </div>
    </div>

    <!-- Devices Table -->
    <GenericTable
      v-if="canView || isStaff"
      ref="table"
      :columns="columns"
      :items="paginatedDevices"
      itemKey="id"
      :selectable="true"
      @update:selectedItems="updateSelectedDevices"
    >
      <!-- Custom cell templates -->
      <template #status="{ value }">
        <span
          :class="[
            'px-3 py-1 rounded-full text-xs font-medium',
            value ? 'bg-green-500 text-white dark:bg-green-500/20 dark:text-green-500 ' : 'bg-red-500 text-white dark:bg-red-500/20 dark:text-red-500 '
          ]"
        >
          {{ value ? 'ONLINE' : 'OFFLINE' }}
        </span>
      </template>
      <template #name="{ value }">
        {{ formatValue("name", value) }}
      </template>
      <template #uptime="{ item }">
        {{ formatUptime(resolveUptime(item)) }}
      </template>
      <template #mac_address="{ value }">
        <span>{{ value }}</span>
      </template>
      <template #last_seen="{ value }">
        {{ formatValue("last_seen", value) }}
      </template>
      <template #created_at="{ value }">
        {{ formatValue("created_at", value) }}
      </template>
      <template #location="{ value }">
        {{ formatValue("location", value) }}
      </template>
      <template #actions="{item}">
        <div class="flex gap-2">
          <button
            v-if="isStaff || canChange"
            class="inline-flex p-2 rounded-lg border border-gray-600 bg-gray-800 hover:bg-gray-700 transition-colors"
            type="button"
            @click.stop="goToDeviceDetail(item)"
            >
            <PencilIcon/>
          </button>

          <button 
            class="inline-flex p-2 rounded-lg border border-gray-600 bg-gray-800 hover:bg-gray-700 transition-colors"
            type="button"
            >
            <NetworkIcon/>
          </button>
        </div>
      </template>
    </GenericTable>

    <!-- Pagination -->
    <div class="flex flex-col gap-4 mt-8">
      <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div class="flex items-center gap-2 w-full sm:w-auto">
          <button
            @click="changePage(-1)"
            :disabled="currentPage === 1"
            class="flex-1 sm:flex-initial px-6 py-3 rounded-lg bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 text-base"
          >
            Previous
          </button>
          <button
            @click="changePage(1)"
            :disabled="currentPage === totalPages"
            class="flex-1 sm:flex-initial px-6 py-3 rounded-lg bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 text-base"
          >
            Next
          </button>
        </div>

        <div class="text-gray-700 dark:text-white text-center">
          Page {{ currentPage }} of {{ totalPages }}
        </div>

        <div class="w-full sm:w-auto">
          <GlassSelect
            v-model="itemsPerPage"
            :options="[{ value: 10, label: '10 per page' }, { value: 20, label: '20 per page' }, { value: 50, label: '50 per page' }, { value: 100, label: '100 per page' }, { value: -1, label: 'All' }]"
            :valueKey="'value'"
            :labelKey="'label'"
            placeholder="Items per page"
            @change="updatePagination"
          />
        </div>
      </div>
    </div>

    <DeleteModal
      v-if="deletingItem"
      :deleting-item="deletingItem"
      :componet-objects="componetObjects"
      :is-deleting="isDeleting"
      @delete="confirmDelete"
      @close="deletingItem = false"
    />
  </div>
</template>


<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import SearchBar from '@/components/SearchBar.vue';
import GlassSelect from '@/components/GlassSelect.vue';
import GenericTable from '@/components/GenericTable.vue';
import GenericMivuLiteTable from '@/components/GenericMivuLiteTable.vue';
import PageError from '@/components/PageError.vue';
import DeleteModal from '@/components/DeleteModal.vue';
import { getDevices, deleteDevice, createDevice } from '@/services/mivulite/devicesService';
import { addToWhitelist } from '@/services/mivulite/whitelistService';
import { useStore } from '@/store';
import { CpuIcon, Wifi, WifiHigh, WifiOff,  AlertTriangle, PencilIcon, GitGraph, Network, NetworkIcon, ChartBarIncreasing, ChartLineIcon  } from 'lucide-vue-next';
import { ChartBarIcon } from '@heroicons/vue/24/outline';
import { useDeviceUptime } from '@/composables/useDeviceUptime';
import api from '@/services/api';

const router = useRouter();

// Refs
const { fetchUptimeMetrics, resolveUptime, formatUptime } = useDeviceUptime()

const showHowToDevice = ref(false);
const devices = ref([]);
const searchQuery = ref("");
const filteredDevices = ref([]);
const currentPage = ref(1);
const itemsPerPage = ref(20);
const selectedDevices = ref([]);
const deletingItem = ref(false);
const bulkAction = ref("");
const canAdd = ref(false);
const canDelete = ref(false);
const canView = ref(false);
const canChange = ref(false)
const error = ref("");
const errorCode = ref("");
const isDeleting = ref(false);
const table = ref(null);
const addingDevice = ref(false)
const newDevice = ref({
  mac_address : '',
})

// Constants
const columns = [
  { key: "name", label: "Name", sortable: true, width: "15%" },
  { key: "mac_address", label: "MAC Address", sortable: true, width: "15%" },
  { key: "status", label: "Status", sortable: true, width: "10%" },
  { key: "uptime", label: "Uptime", sortable: false, width: "12%" },
  { key: "location", label: "Location", sortable: true, width: "15%" },
  { key: "actions", label: "Actions", sortable: true, width: "15%" },
];

const errorMessages = {
  400: "Bad Request. The server could not understand the request.",
  401: "Unauthorized access. Please check your authentication token or log in again.",
  403: "You do not have permission to access this resource. Contact a staff member to assign you the permissions to access this page.",
  404: "Page Not Found.",
  500: "Internal Server Error - Something went wrong on the server.",
};

// Computed
const user = computed(() => useStore.getUser());

const isStaff = computed(() => useStore.isStaff())

const isFiltered = computed(() => searchQuery.value !== "");

const onlineCount = computed(() => 
  devices.value.filter(d => d.is_online).length
);

const offlineCount = computed(() => 
  devices.value.filter(d => !d.is_online).length
);

const alertCount = computed(() => 
  devices.value.filter(d => !d.is_accepted || !d.is_whitelisted).length
);

const totalPages = computed(() => {
  return itemsPerPage.value === -1
    ? 1
    : Math.ceil(filteredDevices.value.length / itemsPerPage.value);
});

const paginatedDevices = computed(() => {
  if (itemsPerPage.value === -1) {
    return filteredDevices.value.map(d => ({
      ...d,
      status: d.is_online
    }));
  }
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredDevices.value.slice(start, end).map(d => ({
    ...d,
    status: d.is_online
  }));
});

const componetObjects = computed(() => {
  return selectedDevices.value.map((device) => ({
    values: [
      { name: "Device", value: device.name },
      { name: "MAC Address", value: device.mac_address },
      { name: "Location", value: device.location || "N/A" },
    ],
  }));
});

// Methods
const handleShowHowToDevice = (value) => {
  showHowToDevice.value = value;
};

const checkPermissions = async () => {
  if (user.value) {
    canAdd.value = await useStore.hasPermission("Can add device");
    canDelete.value = await useStore.hasPermission("Can delete device");
    canView.value = await useStore.hasPermission("Can view device");
    canChange.value = await useStore.hasPermission("Can change device")
  }
};


const createNewDevice = async () => {
  try {
    const response = await addToWhitelist(newDevice.value);
    addingDevice.value = false;
    await fetchDevices();
  } catch (error) {
    console.error(error);
  }
}

const fetchDevices = async () => {
  try {
    devices.value = await getDevices();
    applyFilters();
  } catch (err) {
    if (err.response) {
      error.value = `${errorMessages[err.response.status]}`;
      errorCode.value = `${err.response.status}`;
    } else {
      error.value = `Unknown Error`;
    }
  }
};

const goToDeviceDetail = (device) => {
  router.push({
    name: "DeviceDetails",
    params: { id: device.mac_address },
  });
};


const applyFilters = () => {
  filteredDevices.value = devices.value.filter((device) =>
    device?.name?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    device?.mac_address?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    (device?.location && device.location?.toLowerCase().includes(searchQuery.value.toLowerCase()))
  );
  currentPage.value = 1;
};

const updateSelectedDevices = (selectedItems) => {
  selectedDevices.value = selectedItems;
};

const applyBulkAction = async () => {
  if (bulkAction.value === "delete") {
    deletingItem.value = true;
  }
  bulkAction.value = "";
};

const confirmDelete = async () => {
  isDeleting.value = true;
  await deleteSelectedDevices();
  deletingItem.value = false;
  bulkAction.value = "";
};

const deleteSelectedDevices = async () => {
  if (selectedDevices.value.length === 0) {
    alert("No devices selected for deletion");
    return;
  }

  try {
    const deletePromises = selectedDevices.value.map((device) =>
      deleteDevice(device.id)
    );
    await Promise.all(deletePromises);

    devices.value = devices.value.filter(
      (device) => !selectedDevices.value.some((item) => item.id === device.id)
    );
    table.value?.clearSelection();
    applyFilters();
    selectedDevices.value = [];
  } catch (err) {
    console.error("Error during bulk delete:", err);
    alert("An error occurred while deleting devices. Please try again.");
  } finally {
    isDeleting.value = false;
  }
};

const calculateUptime = (lastSeen) => {
  if (!lastSeen) return "N/A";
  const now = new Date();
  const lastSeenDate = new Date(lastSeen);
  const diff = now - lastSeenDate;
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const formatValue = (key, value) => {
  if (key === "last_seen" || key === "created_at") {
    return formatDateTime(value);
  }
  return value !== null && value !== undefined ? value : "N/A";
};

const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  };
  return new Date(dateString).toLocaleString(undefined, options);
};

const changePage = (delta) => {
  currentPage.value += delta;
};

const updatePagination = () => {
  currentPage.value = 1;
};

// Watchers
watch(searchQuery, () => {
  applyFilters();
});

watch(
  user,
  (newUser) => {
    if (newUser) {
      checkPermissions();
    }
  },
  { immediate: true }
);

// Lifecycle
onMounted(async () => {
  checkPermissions();
  await fetchDevices();
  applyFilters();
});

onMounted(() => fetchUptimeMetrics())

// Expose methods/refs that might be needed by template ref
defineExpose({
  table,
  handleShowHowToDevice,
  goToDeviceDetail,
  updateSelectedDevices,
  applyBulkAction,
  confirmDelete,
  calculateUptime,
  formatValue,
  formatDateTime,
  changePage,
  updatePagination,
});
</script>